import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";

import { adminApi } from "@/lib/api";
import { AdminInput, AdminSelect, AdminTextarea, EmptyState, FieldLabel } from "@/components/admin/AdminUI";
import ImageUploadField from "@/components/admin/ImageUploadField";

const PAGE_ORDER = ["home", "about", "residential", "bim", "contact"];

export default function AdminPagesModule() {
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState("home");
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.listPages().then((docs) => {
      const sorted = [...docs].sort((a, b) => PAGE_ORDER.indexOf(a.slug) - PAGE_ORDER.indexOf(b.slug));
      setPages(sorted);
    });
  }, []);

  useEffect(() => {
    if (!activeSlug) return;
    adminApi.getPage(activeSlug).then((p) => setCurrent(p));
  }, [activeSlug]);

  const updateContent = (content) => setCurrent((prev) => ({ ...prev, content }));

  const save = async () => {
    if (!current) return;

    setSaving(true);

    try {
      const updated = await adminApi.updatePage(current.slug, {
        title: current.title,
        content: current.content || {},
        seo: current.seo,
        status: current.status,
      });

      setCurrent(updated);
      toast.success("Page saved.");
    } catch (e) {
      toast.error("Failed to save page.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-3">
        <div className="admin-card rounded-sm p-3 sticky top-24">
          <p className="eyebrow-muted px-2 pt-2">[ Pages ]</p>

          <ul className="mt-3 space-y-1">
            {pages.map((p) => (
              <li key={p.slug}>
                <button
                  type="button"
                  onClick={() => setActiveSlug(p.slug)}
                  className={`w-full text-left flex items-center justify-between px-3 py-2.5 rounded-sm text-sm font-medium ${
                    activeSlug === p.slug
                      ? "bg-brand-yellow/12 text-brand-yellow"
                      : "text-brand-white/85 hover:bg-white/[0.03]"
                  }`}
                  data-testid={`admin-page-${p.slug}`}
                >
                  <span className="capitalize">{p.title}</span>
                  <span className="text-[10px] uppercase tracking-widest text-brand-gray">{p.status}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <section className="lg:col-span-9">
        {!current ? (
          <EmptyState title="Select a page to edit" />
        ) : (
          <div className="admin-card rounded-sm p-5 md:p-6 space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="eyebrow-muted">[ Visual Content Editor ]</p>
                <h2 className="text-2xl font-semibold mt-1 capitalize">{current.title}</h2>
                <p className="text-xs text-brand-gray mt-1">/{current.slug}</p>
              </div>

              <button type="button" onClick={save} disabled={saving} className="btn-primary disabled:opacity-60">
                {saving ? "Saving…" : "Save Page"}
                <Save className="h-4 w-4" />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <label>
                <FieldLabel>Page title</FieldLabel>
                <AdminInput
                  value={current.title || ""}
                  onChange={(e) => setCurrent({ ...current, title: e.target.value })}
                />
              </label>

              <label>
                <FieldLabel>Status</FieldLabel>
                <AdminSelect value={current.status} onChange={(e) => setCurrent({ ...current, status: e.target.value })}>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </AdminSelect>
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <label>
                <FieldLabel>SEO Title</FieldLabel>
                <AdminInput
                  value={current.seo?.title || ""}
                  onChange={(e) =>
                    setCurrent({
                      ...current,
                      seo: { ...(current.seo || {}), title: e.target.value },
                    })
                  }
                />
              </label>

              <label>
                <FieldLabel>SEO Description</FieldLabel>
                <AdminInput
                  value={current.seo?.description || ""}
                  onChange={(e) =>
                    setCurrent({
                      ...current,
                      seo: { ...(current.seo || {}), description: e.target.value },
                    })
                  }
                />
              </label>
            </div>

            <div>
              <FieldLabel>Page sections</FieldLabel>
              <p className="text-[11px] text-brand-gray mt-1 mb-4">
                Edit titles, paragraphs, CTA labels, image fields, BIM galleries and nested page content without touching code.
              </p>

              <ObjectEditor value={current.content || {}} onChange={updateContent} level={0} />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function ObjectEditor({ value, onChange, level = 0 }) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  return (
    <div className={`space-y-4 ${level > 0 ? "border border-white/8 rounded-sm p-4 bg-white/[0.015]" : ""}`}>
      {Object.entries(value).map(([key, val]) => (
        <FieldEditor
          key={key}
          fieldKey={key}
          value={val}
          level={level}
          onChange={(next) => onChange({ ...value, [key]: next })}
          onRemove={
            level > 0
              ? () => {
                  const copy = { ...value };
                  delete copy[key];
                  onChange(copy);
                }
              : null
          }
        />
      ))}
    </div>
  );
}

function FieldEditor({ fieldKey, value, onChange, onRemove, level }) {
  const label = humanize(fieldKey);
  const fieldIsImage = isImageField(fieldKey);

  if (Array.isArray(value)) {
    return (
      <div className="border border-white/8 rounded-sm p-4 bg-ink-black/25">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <FieldLabel>{label}</FieldLabel>
            {fieldIsImage ? (
              <p className="text-[11px] text-brand-gray mt-1">
                Upload background images directly from your computer.
              </p>
            ) : null}
          </div>

          <button
            type="button"
            className="btn-outline text-[10px] px-3 py-2"
            onClick={() => onChange([...value, fieldIsImage ? "" : cloneArrayItem(value)])}
          >
            <Plus className="h-3.5 w-3.5" />
            Add item
          </button>
        </div>

        <div className={fieldIsImage ? "grid md:grid-cols-2 gap-3" : "space-y-3"}>
          {value.map((item, index) => (
            <div key={index} className="border border-white/8 rounded-sm p-3 bg-white/[0.015]">
              <div className="flex items-center justify-between mb-3">
                <span className="eyebrow-muted">Item {String(index + 1).padStart(2, "0")}</span>

                <button
                  type="button"
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                  className="text-brand-gray hover:text-brand-red"
                  aria-label={`Remove ${label} item ${index + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {typeof item === "object" && item !== null ? (
                <ObjectEditor
                  value={item}
                  level={level + 1}
                  onChange={(next) => {
                    const arr = [...value];
                    arr[index] = next;
                    onChange(arr);
                  }}
                />
              ) : (
                <PrimitiveField
                  value={item}
                  fieldKey={fieldKey}
                  onChange={(next) => {
                    const arr = [...value];
                    arr[index] = next;
                    onChange(arr);
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (value && typeof value === "object") {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <FieldLabel>{label}</FieldLabel>

          {onRemove ? (
            <button type="button" onClick={onRemove} className="text-brand-gray hover:text-brand-red">
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <ObjectEditor value={value} onChange={onChange} level={level + 1} />
      </div>
    );
  }

  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <PrimitiveField value={value} onChange={onChange} fieldKey={fieldKey} />
    </label>
  );
}

function PrimitiveField({ value, onChange, fieldKey = "" }) {
  const stringValue = value ?? "";
  const isLong = String(stringValue).length > 80 || /paragraph|description|subheadline|quote|intro|text/i.test(fieldKey);

  if (typeof value === "boolean") {
    return (
      <AdminSelect value={String(value)} onChange={(e) => onChange(e.target.value === "true")}>
        <option value="true">True</option>
        <option value="false">False</option>
      </AdminSelect>
    );
  }

  if (typeof value === "number") {
    return <AdminInput type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} />;
  }

  if (isImageField(fieldKey)) {
    return (
      <ImageUploadField
        value={stringValue}
        onChange={onChange}
        category={guessImageCategory(fieldKey)}
        placeholder="Upload image or paste image URL"
      />
    );
  }

  if (isLong) {
    return <AdminTextarea rows={5} value={stringValue} onChange={(e) => onChange(e.target.value)} />;
  }

  return <AdminInput value={stringValue} onChange={(e) => onChange(e.target.value)} />;
}

function isImageField(fieldKey = "") {
  return /(^url$|image|images|background|backgrounds|portrait|cover|logo|og_image|photo|photos|picture|gallery)/i.test(fieldKey);
}

function guessImageCategory(fieldKey) {
  if (/team|portrait/i.test(fieldKey)) return "team";
  if (/bim|technical/i.test(fieldKey)) return "bim";
  if (/logo/i.test(fieldKey)) return "logo";
  if (/residential|cover|gallery/i.test(fieldKey)) return "residential";

  return "general";
}

function cloneArrayItem(arr) {
  if (!arr.length) return "";

  const sample = arr[arr.length - 1];

  if (Array.isArray(sample)) return [];

  if (sample && typeof sample === "object") {
    return Object.fromEntries(
      Object.entries(sample).map(([k, v]) => [
        k,
        typeof v === "number"
          ? 0
          : typeof v === "boolean"
          ? false
          : Array.isArray(v)
          ? []
          : v && typeof v === "object"
          ? {}
          : "",
      ])
    );
  }

  return "";
}

function humanize(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}